import type { ApolloError } from '@apollo/client';
import type { FragmentType } from 'src/gql';
import type { AssetFields } from 'src/types/asset';
import { Text } from '@savvy-kit/foundations/text';
import { useFormik } from 'formik';
import MonetaryInputTextField from 'src/components/mui/monetary-input-text-field';
import TrackedButton from 'src/components/tracked/tracked-button';
import { graphql, unmaskFragment } from 'src/gql';
import { number as yupNumber, object as yupObject } from 'yup';

export const CompanyEquityValueFormFragment = graphql(/* GraphQL */ `
  fragment CompanyEquityValueForm on Asset {
    value {
      amount
    }
  }
`);

export default function CompanyEquityValueForm({
  assetFrag,
  assetFields,
  createError,
  updateError,
  isEditMode,
  onNext,
}: {
  readonly assetFrag?: FragmentType<typeof CompanyEquityValueFormFragment>;
  readonly assetFields?: AssetFields;
  readonly isEditMode: boolean;
  readonly createError: ApolloError | undefined;
  readonly updateError: ApolloError | undefined;
  readonly onNext: (assetFields: AssetFields) => void;
}) {
  const asset = unmaskFragment(CompanyEquityValueFormFragment, assetFrag);
  const formik = useFormik({
    initialValues: {
      currentValue: assetFields?.currentValue ?? asset?.value.amount ?? '',
    },
    validationSchema: yupObject({
      currentValue: yupNumber().required().positive().required('Value is required'),
    }),
    onSubmit: () => {
      onNext({ ...formik.values });
    },
  });

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <div className="flex flex-col gap-6">
        <Text variant="body-sm" color="secondary">
          You can find the value in the equity management tool that your employer uses, such as Carta or Shareworks. If
          not, most people use the value of their vested shares.
        </Text>

        <MonetaryInputTextField
          error={Boolean(formik.touched.currentValue && formik.errors.currentValue)}
          autoFocus={!asset?.value.amount}
          errorMessage={formik.errors.currentValue}
          label="Value"
          name="currentValue"
          onBlur={formik.handleBlur}
          formik={formik}
          value={formik.values.currentValue}
        />
        {createError && (
          <Text variant="caption" color="destructive" render={<p />}>
            Error adding equity: {createError.message}
          </Text>
        )}
        {updateError && (
          <Text variant="caption" color="destructive" render={<p />}>
            Error editing equity: {updateError.message}
          </Text>
        )}
      </div>
      <div className="flex flex-row justify-end my-6">
        <TrackedButton
          elementName={
            isEditMode ? 'update-company-equity-value-form-button' : 'create-company-equity-value-form-button'
          }
          disabled={!isEditMode && (formik.isSubmitting || !formik.isValid)}
          type="submit"
        >
          {isEditMode ? `Edit equity` : `Add equity`}
        </TrackedButton>
      </div>
    </form>
  );
}
